import { useCallback } from 'react';
import Swal from 'sweetalert2';
import axios from 'axios';
import { deleteLocalCosyflow, deleteUserCosyflow } from '../api-helper';
import electronBridge from '../utils/electronBridge';
import { db } from '../db';
import { removeCosyflowFromMemoryCache } from '../services/cosyflowsCacheService';

/**
 * Remove a deleted cosyflow from all caches (IndexedDB settings caches + db.cosyflows)
 */
async function removeCosyflowFromCaches(cosyflowUid, cosyflowTitle) {
    const cacheKeys = ['cosyflows_api_cache', 'cosyflows_api_cache_v3'];

    for (const key of cacheKeys) {
        try {
            const cached = await db.settings.get(key);
            if (cached?.value) {
                let changed = false;
                const updated = { ...cached.value };

                if (updated.cosyflows && Array.isArray(updated.cosyflows)) {
                    const before = updated.cosyflows.length;
                    updated.cosyflows = updated.cosyflows.filter(c =>
                        (c.uid || c.id) !== cosyflowUid && c.title !== cosyflowTitle
                    );
                    if (updated.cosyflows.length < before) changed = true;
                }

                if (updated.transformedCosyflows && Array.isArray(updated.transformedCosyflows)) {
                    const before = updated.transformedCosyflows.length;
                    updated.transformedCosyflows = updated.transformedCosyflows.filter(c =>
                        (c.uid || c.id) !== cosyflowUid && c.title !== cosyflowTitle
                    );
                    if (updated.transformedCosyflows.length < before) changed = true;
                }

                if (changed) {
                    await db.settings.put({ key, value: updated });
                    console.log(`[removeCosyflowFromCaches] Removed from ${key} cache`);
                }
            }
        } catch (e) {
            console.warn(`[removeCosyflowFromCaches] Error updating ${key}:`, e);
        }
    }

    // Also remove from db.cosyflows table
    try {
        const match = await db.cosyflows.where('data.title').equals(cosyflowTitle).first();
        if (match) {
            await db.cosyflows.delete(match.id);
            console.log('[removeCosyflowFromCaches] Removed from db.cosyflows');
        }
    } catch (e) {
        console.warn('[removeCosyflowFromCaches] Error removing from db.cosyflows:', e);
    }
}

export const useCosyflowHandlers = ({
    navigate,
    accountNo,
    accountAlias,
    startInstall,
    updateCosyflow,
    onOpenEditModal,
    onOpenWizard,
    onRefresh,
}) => {
    const extractWorkflow = useCallback((cosyflow) => {
        let workflowToLoad = null;

        if (cosyflow.cosyflow) {
            try {
                const cosyflowDef = typeof cosyflow.cosyflow === 'string'
                    ? JSON.parse(cosyflow.cosyflow)
                    : cosyflow.cosyflow;

                console.log('[useCosyflowHandlers] Parsed cosyflow definition:', cosyflowDef);

                if (cosyflowDef?.workflow) {
                    workflowToLoad = cosyflowDef.workflow;
                    console.log('[useCosyflowHandlers] Found workflow in cosyflow definition');
                }
            } catch (e) {
                console.error('[useCosyflowHandlers] Error parsing cosyflow:', e);
            }
        }

        if (!workflowToLoad && cosyflow.workflow) {
            workflowToLoad = cosyflow.workflow;
            console.log('[useCosyflowHandlers] Found workflow directly on cosyflow object');
        }

        return workflowToLoad;
    }, []);

    const handleRunInPlayground = useCallback((cosyflow) => {
        console.log('[useCosyflowHandlers] Run in Playground:', cosyflow.title);
        sessionStorage.setItem('deepLinkModel', cosyflow.title);
        sessionStorage.setItem('deepLinkFilterMode', 'online');
        navigate('/');
    }, [navigate]);

    const handleRunOfflineInPlayground = useCallback((cosyflow) => {
        console.log('[useCosyflowHandlers] Run in Playground (Local):', cosyflow.title);
        sessionStorage.setItem('deepLinkModel', `${cosyflow.title}__local_cosy`);
        sessionStorage.setItem('deepLinkFilterMode', 'offline');
        navigate('/');
    }, [navigate]);

    const handleRunInCosyUI = useCallback((cosyflow) => {
        console.log('[useCosyflowHandlers] Load in CosyUI:', cosyflow.title);
        console.log('[useCosyflowHandlers] Cosyflow data:', cosyflow);

        const workflowToLoad = extractWorkflow(cosyflow);

        if (!workflowToLoad) {
            console.warn('[useCosyflowHandlers] No workflow found in cosyflow:', cosyflow);
            Swal.fire({
                title: 'No Workflow Found',
                text: 'This cosyflow does not contain a workflow.',
                icon: 'warning',
                confirmButtonColor: '#FFB02E'
            });
            return;
        }

        console.log('[useCosyflowHandlers] Workflow to load:', workflowToLoad);

        navigate('/cosyui', {
            state: {
                workflowToLoad: workflowToLoad,
                cosyflowTitle: cosyflow.title
            }
        });
    }, [navigate, extractWorkflow]);

    const handleRunInComfyUI = useCallback((cosyflow) => {
        console.log('[useCosyflowHandlers] Load in ComfyUI:', cosyflow.title);
        console.log('[useCosyflowHandlers] Cosyflow data:', cosyflow);

        const workflowToLoad = extractWorkflow(cosyflow);

        if (!workflowToLoad) {
            console.warn('[useCosyflowHandlers] No workflow found in cosyflow:', cosyflow);
            Swal.fire({
                title: 'No Workflow Found',
                text: 'This cosyflow does not contain a workflow.',
                icon: 'warning',
                confirmButtonColor: '#FFB02E'
            });
            return;
        }

        console.log('[useCosyflowHandlers] Workflow to load in ComfyUI:', workflowToLoad);

        navigate('/comfyui', {
            state: {
                workflowToLoad: workflowToLoad,
                cosyflowTitle: cosyflow.title
            }
        });
    }, [navigate, extractWorkflow]);

    const handleCreateFromDistributed = useCallback((cosyflow) => {
        console.log('[useCosyflowHandlers] Open in Cosyflow Editor:', cosyflow.title);
        console.log('[useCosyflowHandlers] Full cosyflow object:', cosyflow);
        console.log('[useCosyflowHandlers] cosyflow.cosyflow exists:', !!cosyflow.cosyflow);
        console.log('[useCosyflowHandlers] cosyflow.cosyflow type:', typeof cosyflow.cosyflow);

        let innerCosyflow = cosyflow.cosyflow;
        if (typeof innerCosyflow === 'string') {
            try {
                innerCosyflow = JSON.parse(innerCosyflow);
                console.log('[useCosyflowHandlers] Parsed inner cosyflow:', innerCosyflow);
            } catch (e) {
                console.error('[useCosyflowHandlers] Failed to parse inner cosyflow:', e);
            }
        } else {
            console.log('[useCosyflowHandlers] Inner cosyflow (already object):', innerCosyflow);
        }

        console.log('[useCosyflowHandlers] Inner cosyflow has variables:', !!innerCosyflow?.variables);
        console.log('[useCosyflowHandlers] Inner cosyflow has workflow:', !!innerCosyflow?.workflow);
        console.log('[useCosyflowHandlers] Inner cosyflow has prompt:', !!innerCosyflow?.prompt);

        const cosyflowWithFlags = {
            ...cosyflow,
            account_no: cosyflow.accountNo || accountNo,
            trusted: cosyflow.trusted ? '1' : '0',
            public: cosyflow.isPublic ? '1' : '0',
            distributed: cosyflow.distributed ? '1' : '0'
        };

        console.log('[useCosyflowHandlers] Final cosyflowWithFlags:', cosyflowWithFlags);

        navigate('/cosyflow-editor', {
            state: {
                importedCosyflow: cosyflowWithFlags,
                isNewFromComfyUI: false
            }
        });
    }, [navigate, accountNo]);

    const handleOpenEditor = useCallback((cosyflow) => {
        console.log('[useCosyflowHandlers] Open CosyFlow Editor:', cosyflow.title);
        const isMyCosyflow = cosyflow.accountNo === accountNo;
        const cosyflowWithFlags = {
            ...cosyflow,
            trusted: isMyCosyflow ? (cosyflow.trusted ? '1' : '0') : '1',
            public: isMyCosyflow ? (cosyflow.isPublic ? '1' : '0') : '1',
            distributed: cosyflow.distributed ? '1' : '0'
        };
        navigate('/cosyflow-editor', { state: { importedCosyflow: cosyflowWithFlags } });
    }, [navigate, accountNo]);

    const handleMakeAvailableLocally = useCallback((cosyflow) => {
        console.log('[useCosyflowHandlers] Make Available Locally:', cosyflow.title);
        Swal.fire({
            title: 'Install Locally',
            text: `This will install "${cosyflow.title}" to your local CosyUI/ComfyUI instance. Make sure your local server is running.`,
            icon: 'info',
            showCancelButton: true,
            confirmButtonColor: '#14B8A6',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Install'
        }).then((result) => {
            if (result.isConfirmed) {
                navigate('/cosyui', {
                    state: {
                        installCosyflow: cosyflow,
                        cosyflowTitle: cosyflow.title
                    }
                });
            }
        });
    }, [navigate]);

    const handleMakeAvailableOnline = useCallback((cosyflow) => {
        console.log('[useCosyflowHandlers] Make Available Online:', cosyflow.title);
        Swal.fire({
            title: 'Make Available Online',
            text: `This will submit "${cosyflow.title}" to be made available on Promptus cloud servers. This requires review and approval.`,
            icon: 'info',
            showCancelButton: true,
            confirmButtonColor: '#6366F1',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Submit'
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Submitted',
                    text: 'Your CosyFlow has been submitted for review.',
                    icon: 'success',
                    confirmButtonColor: '#FFB02E'
                });
            }
        });
    }, []);

    const handleCreateTemplate = useCallback((cosyflow) => {
        console.log('[useCosyflowHandlers] Create Template from:', cosyflow.title);

        const username = accountAlias;
        if (!username || username.trim() === '') {
            Swal.fire({
                title: 'Username Required',
                html: `
                    <p style="margin-bottom: 15px;">You need to register a username before creating templates.</p>
                    <p>Please go to <strong>Profile</strong> → <strong>Hosting</strong> and register your username.</p>
                `,
                icon: 'warning',
                confirmButtonText: 'Go to Profile',
                showCancelButton: true,
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#f5c518',
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/profile', { state: { openTab: 'hosting' } });
                }
            });
            return;
        }

        if (onOpenWizard) {
            onOpenWizard({
                uid: cosyflow.uid || cosyflow.id,
                title: cosyflow.title,
                description: cosyflow.description,
                category: cosyflow.category,
                usernamePrefix: username,
            });
        }
    }, [accountAlias, navigate, onOpenWizard]);

    const handleTogglePublic = useCallback(async (cosyflow, newValue) => {
        console.log('[useCosyflowHandlers] Toggle Public:', cosyflow.title, '→', newValue);

        if (!updateCosyflow) {
            console.warn('[useCosyflowHandlers] updateCosyflow not provided');
            return;
        }

        try {
            const result = await updateCosyflow({
                accountNo: accountNo,
                uid: cosyflow.uid || cosyflow.id,
                title: cosyflow.title,
                description: cosyflow.description,
                category: cosyflow.category,
                isPublic: newValue,
                distributed: cosyflow.distributed || false,
            });

            if (result.error) {
                Swal.fire({
                    title: 'Error',
                    text: result.error,
                    icon: 'error',
                    confirmButtonColor: '#FFB02E'
                });
                return;
            }

            Swal.fire({
                title: 'Updated!',
                text: `Cosyflow is now ${newValue ? 'public' : 'private'}.`,
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
            });

            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('[useCosyflowHandlers] Error toggling public:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to update cosyflow.',
                icon: 'error',
                confirmButtonColor: '#FFB02E'
            });
        }
    }, [accountNo, updateCosyflow, onRefresh]);

    const handleToggleDistributed = useCallback(async (cosyflow, newValue) => {
        console.log('[useCosyflowHandlers] Toggle Distributed:', cosyflow.title, '→', newValue);

        if (!updateCosyflow) {
            console.warn('[useCosyflowHandlers] updateCosyflow not provided');
            return;
        }

        try {
            const result = await updateCosyflow({
                accountNo: accountNo,
                uid: cosyflow.uid || cosyflow.id,
                title: cosyflow.title,
                description: cosyflow.description,
                category: cosyflow.category,
                isPublic: cosyflow.isPublic !== false,
                distributed: newValue,
            });

            if (result.error) {
                Swal.fire({
                    title: 'Error',
                    text: result.error,
                    icon: 'error',
                    confirmButtonColor: '#FFB02E'
                });
                return;
            }

            Swal.fire({
                title: 'Updated!',
                text: `Cosyflow is now ${newValue ? 'distributed' : 'not distributed'}.`,
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
            });

            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('[useCosyflowHandlers] Error toggling distributed:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to update cosyflow.',
                icon: 'error',
                confirmButtonColor: '#FFB02E'
            });
        }
    }, [accountNo, updateCosyflow, onRefresh]);

    const handleInstallLocally = useCallback(async (cosyflow) => {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000);

            const response = await axios.get('http://localhost:7412/status', {
                signal: controller.signal,
                timeout: 2000
            });
            clearTimeout(timeoutId);

            const statuses = Array.isArray(response.data) ? response.data : [];
            const cosyServer = statuses.find(s => s.name === 'cosy' && s.status === 'ready');

            if (!cosyServer) {
                Swal.fire({
                    title: 'Server Not Available',
                    text: 'Local Cosyflow server not detected or not ready. Please ensure your local server is running.',
                    icon: 'error',
                    confirmButtonColor: '#FFB02E',
                });
                return;
            }

            if (startInstall) {
                startInstall(cosyflow, cosyServer.url);
            } else if (electronBridge.isElectron) {
                electronBridge.ipcRenderer.send('install-cosyflow', { uid: cosyflow.uid, title: cosyflow.title });
            }

        } catch (error) {
            if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
                console.error(`[useCosyflowHandlers] Install Server Not Available: Could not connect to server manager. Please ensure Promptus Manager is running`)
                Swal.fire({
                    title: 'Server Not Available',
                    text: 'Could not connect to server manager. Please ensure Promptus Manager is running.',
                    icon: 'error',
                    confirmButtonColor: '#FFB02E',
                });
            } else {
                console.error(`[useCosyflowHandlers] Install failed error: `, error.response?.data?.message || error.message || 'Failed to install cosyflow locally.')
                Swal.fire({
                    title: 'Installation Failed',
                    text: 'Failed to install cosyflow locally.',
                    icon: 'error',
                    confirmButtonColor: '#FFB02E',
                });
            }
        }
    }, [startInstall]);

    const handleModifyCosyflow = useCallback((cosyflow) => {
        console.log('[useCosyflowHandlers] Modify Cosyflow:', cosyflow.title);
        if (onOpenEditModal) {
            onOpenEditModal(cosyflow);
        }
    }, [onOpenEditModal]);

    const handleDeleteCosyflow = useCallback(async (cosyflow) => {
        console.log('[useCosyflowHandlers] handleDeleteCosyflow called with cosyflow:', {
            title: cosyflow.title,
            uid: cosyflow.uid,
            accountNo: cosyflow.accountNo,
            isLocal: cosyflow.isLocal,
            fullCosyflow: cosyflow
        });

        const result = await Swal.fire({
            title: 'Delete Cosyflow?',
            text: `Are you sure you want to delete "${cosyflow.title}"? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it',
            cancelButtonText: 'Cancel',
            background: '#190A4E',
            color: '#FFFFFF',
        });

        console.log('[useCosyflowHandlers] Swal result:', result);

        if (result.isConfirmed) {
            try {
                console.log('[useCosyflowHandlers] Delete confirmed, checking isLocal:', cosyflow.isLocal);

                if (cosyflow.isLocal) {
                    console.log('[useCosyflowHandlers] LOCAL PATH - Attempting to delete local cosyflow');

                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 2000);

                    console.log('[useCosyflowHandlers] LOCAL PATH - Fetching server status from http://localhost:7412/status');
                    const statusResponse = await axios.get('http://localhost:7412/status', {
                        signal: controller.signal,
                        timeout: 2000
                    });
                    clearTimeout(timeoutId);

                    console.log('[useCosyflowHandlers] LOCAL PATH - Server status response:', statusResponse.data);

                    const statuses = Array.isArray(statusResponse.data) ? statusResponse.data : [];
                    const cosyServer = statuses.find(s => s.name === 'cosy' && s.status === 'ready');

                    console.log('[useCosyflowHandlers] LOCAL PATH - Found cosy server:', cosyServer);

                    if (!cosyServer) {
                        console.log('[useCosyflowHandlers] LOCAL PATH - No cosy server found or not ready');
                        Swal.fire({
                            title: 'Server Not Available',
                            text: 'Local Cosyflow server not detected or not ready. Please ensure your local server is running.',
                            icon: 'error',
                            confirmButtonColor: '#FFB02E',
                            background: '#190A4E',
                            color: '#FFFFFF',
                        });
                        return;
                    }

                    console.log('[useCosyflowHandlers] LOCAL PATH - Calling deleteLocalCosyflow with URL:', cosyServer.url, 'and title:', cosyflow.title);
                    const response = await deleteLocalCosyflow(cosyServer.url, cosyflow.title);
                    console.log('[useCosyflowHandlers] LOCAL PATH - Delete Local Cosyflow Response:', response);

                    const Toast = Swal.mixin({
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000,
                        timerProgressBar: true,
                        background: '#FFFFFF',
                        color: '#190A4E'
                    });

                    if (response?.message === 'uninstalled') {
                        await removeCosyflowFromCaches(cosyflow.uid, cosyflow.title);
                        removeCosyflowFromMemoryCache(cosyflow.uid, cosyflow.title);

                        Toast.fire({
                            icon: 'success',
                            title: 'Local cosyflow deleted successfully'
                        });
                        if (onRefresh) {
                            console.log('[useCosyflowHandlers] LOCAL PATH - Calling onRefresh');
                            onRefresh();
                        }
                    } else if (response?.error == true) {
                        Toast.fire({
                            icon: 'error',
                            title: 'Local cosyflow deleted unsuccessfully'
                        });
                    }


                } else {
                    console.log('[useCosyflowHandlers] ONLINE PATH - Attempting to delete online cosyflow');
                    console.log('[useCosyflowHandlers] ONLINE PATH - Calling deleteUserCosyflow with uid:', cosyflow.uid, 'and accountNo:', cosyflow.accountNo);

                    const response = await deleteUserCosyflow(cosyflow.uid, cosyflow.accountNo);
                    console.log('[useCosyflowHandlers] ONLINE PATH - Delete Cosyflow Response:', response);

                    if (response?.deleted === true) {
                        // Remove from all caches immediately so it disappears without waiting for API refetch
                        await removeCosyflowFromCaches(cosyflow.uid, cosyflow.title);
                        removeCosyflowFromMemoryCache(cosyflow.uid, cosyflow.title);

                        const Toast = Swal.mixin({
                            toast: true,
                            position: 'top-end',
                            showConfirmButton: false,
                            timer: 3000,
                            timerProgressBar: true,
                            background: '#FFFFFF',
                            color: '#190A4E',
                        });
                        Toast.fire({
                            icon: 'success',
                            title: 'Cosyflow deleted successfully'
                        });

                        if (onRefresh) {
                            console.log('[useCosyflowHandlers] ONLINE PATH - Calling onRefresh');
                            onRefresh();
                        }
                    } else {
                        Swal.fire({
                            title: 'Error',
                            text: 'Failed to delete cosyflow. Please try again.',
                            icon: 'error',
                            confirmButtonColor: '#FFB02E',
                            background: '#190A4E',
                            color: '#FFFFFF',
                        });
                    }

                }
            } catch (error) {
                console.error('[useCosyflowHandlers] Error deleting cosyflow:', error);
                console.error('[useCosyflowHandlers] Error details:', {
                    name: error.name,
                    code: error.code,
                    message: error.message,
                    response: error.response?.data
                });

                if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
                    Swal.fire({
                        title: 'Server Not Available',
                        text: 'Could not connect to server manager. Please ensure Promptus Manager is running.',
                        icon: 'error',
                        confirmButtonColor: '#FFB02E',
                        background: '#190A4E',
                        color: '#FFFFFF',
                    });
                } else {
                    Swal.fire({
                        title: 'Error',
                        text: error.response?.data?.message || 'Failed to delete cosyflow. Please try again.',
                        icon: 'error',
                        confirmButtonColor: '#FFB02E',
                        background: '#190A4E',
                        color: '#FFFFFF',
                    });
                }
            }
        }
    }, [onRefresh]);

    return {
        extractWorkflow,
        handleRunInPlayground,
        handleRunOfflineInPlayground,
        handleRunInCosyUI,
        handleRunInComfyUI,
        handleCreateFromDistributed,
        handleOpenEditor,
        handleMakeAvailableLocally,
        handleMakeAvailableOnline,
        handleCreateTemplate,
        handleTogglePublic,
        handleToggleDistributed,
        handleInstallLocally,
        handleModifyCosyflow,
        handleDeleteCosyflow,
    };
};

export default useCosyflowHandlers;
